import { webglTileRenderer } from './webgl-tile-renderer.js'
import { DEM_TILE_SIZE } from './dem-config.js'

const TILE_SIZE = DEM_TILE_SIZE
const PROGRAM_KEY_PREPARE = 'slope-prepare'
const PROGRAM_KEY_DISPLAY = 'slope-display'

const FRAGMENT_PREPARE = `
precision highp float;
varying vec2 v_texCoord;
uniform sampler2D u_dem;
uniform float u_cellSize;
uniform vec2 u_pixelSize;
uniform float u_tileSize;
uniform float u_paddedSize;

float decodeTerrarium(vec4 texel) {
  return texel.r * 255.0 * 256.0 + texel.g * 255.0 + texel.b * 255.0 / 256.0 - 32768.0;
}

// 3x3 Gaussian smooth of the decoded elevation around uv. Mirrors the CPU worker's
// decodeAndSmooth (weights 1,2,1 / 2,4,2 / 1,2,1 / 16) so the GPU (Chromium) and CPU
// (Firefox fallback) paths produce identical slopes. Without this, raw single-pixel DEM
// steps over-steepen at low zoom and saturate whole tiles to the 50+ deg (black) bucket.
float smoothElev(vec2 uv) {
  float c  = decodeTerrarium(texture2D(u_dem, uv));
  float l  = decodeTerrarium(texture2D(u_dem, uv + vec2(-u_pixelSize.x,           0.0)));
  float r  = decodeTerrarium(texture2D(u_dem, uv + vec2( u_pixelSize.x,           0.0)));
  float t  = decodeTerrarium(texture2D(u_dem, uv + vec2(          0.0, -u_pixelSize.y)));
  float b  = decodeTerrarium(texture2D(u_dem, uv + vec2(          0.0,  u_pixelSize.y)));
  float tl = decodeTerrarium(texture2D(u_dem, uv + vec2(-u_pixelSize.x, -u_pixelSize.y)));
  float tr = decodeTerrarium(texture2D(u_dem, uv + vec2( u_pixelSize.x, -u_pixelSize.y)));
  float bl = decodeTerrarium(texture2D(u_dem, uv + vec2(-u_pixelSize.x,  u_pixelSize.y)));
  float br = decodeTerrarium(texture2D(u_dem, uv + vec2( u_pixelSize.x,  u_pixelSize.y)));
  return (4.0 * c + 2.0 * (l + r + t + b) + (tl + tr + bl + br)) / 16.0;
}

void main() {
  vec2 demUV = (v_texCoord * u_tileSize + 1.0) / u_paddedSize;

  float zTL = smoothElev(demUV + vec2(-u_pixelSize.x, -u_pixelSize.y));
  float zT  = smoothElev(demUV + vec2(          0.0, -u_pixelSize.y));
  float zTR = smoothElev(demUV + vec2( u_pixelSize.x, -u_pixelSize.y));
  float zL  = smoothElev(demUV + vec2(-u_pixelSize.x,           0.0));
  float zR  = smoothElev(demUV + vec2( u_pixelSize.x,           0.0));
  float zBL = smoothElev(demUV + vec2(-u_pixelSize.x,  u_pixelSize.y));
  float zB  = smoothElev(demUV + vec2(          0.0,  u_pixelSize.y));
  float zBR = smoothElev(demUV + vec2( u_pixelSize.x,  u_pixelSize.y));

  float dzdx = ((zTR + 2.0 * zR + zBR) - (zTL + 2.0 * zL + zBL)) / (8.0 * u_cellSize);
  float dzdy = ((zBL + 2.0 * zB + zBR) - (zTL + 2.0 * zT + zTR)) / (8.0 * u_cellSize);
  float slopeDeg = atan(sqrt(dzdx * dzdx + dzdy * dzdy)) * 57.29577951;

  gl_FragColor = vec4(slopeDeg / 90.0, 0.0, 0.0, 1.0);
}
`

const FRAGMENT_DISPLAY = `
precision highp float;
varying vec2 v_texCoord;
uniform sampler2D u_slopeMap;

vec3 colorForSlope(float deg) {
  vec3 c0 = vec3(234.0, 179.0,   8.0);
  vec3 c1 = vec3(249.0, 115.0,  22.0);
  vec3 c2 = vec3(239.0,  68.0,  68.0);
  vec3 c3 = vec3(147.0,  51.0, 234.0);
  vec3 c4 = vec3( 59.0, 130.0, 246.0);
  vec3 c5 = vec3( 15.0,  15.0,  15.0);

  // Continuous ramp, linear in angle per segment, matching SLOPE_STOPS in slope-worker.ts:
  // 27→30 yellow→orange (3deg), then 5deg each: 30→35 orange→red, 35→40 red→purple,
  // 40→45 purple→blue, 45→50 blue→black. The smooth fade is the caution signal.
  vec3 color = c0;
  color = mix(color, c1, clamp((deg - 27.0) / 3.0, 0.0, 1.0));
  color = mix(color, c2, clamp((deg - 30.0) / 5.0, 0.0, 1.0));
  color = mix(color, c3, clamp((deg - 35.0) / 5.0, 0.0, 1.0));
  color = mix(color, c4, clamp((deg - 40.0) / 5.0, 0.0, 1.0));
  color = mix(color, c5, clamp((deg - 45.0) / 5.0, 0.0, 1.0));
  return color / 255.0;
}

void main() {
  float slopeDeg = texture2D(u_slopeMap, v_texCoord).r * 90.0;

  if (slopeDeg < 25.0) {
    gl_FragColor = vec4(0.0);
    return;
  }

  // Solid shading begins at 27deg to match the map key (ramp starts at 27deg, nothing
  // painted below 27). 25-27deg is a narrow opacity-only feather so the lower edge is
  // anti-aliased instead of a hard cutoff — no real colour is painted below the key floor.
  float alpha = smoothstep(25.0, 27.0, slopeDeg) * (170.0 / 255.0);
  vec3 color = colorForSlope(slopeDeg);
  gl_FragColor = vec4(color, alpha);
}
`

function metersPerPixel(lat: number, zoom: number): number {
  return ((156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom)) * (256 / TILE_SIZE)
}

function tileToLat(y: number, z: number): number {
  const n = Math.PI - (2 * Math.PI * y) / Math.pow(2, z)
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)))
}

export function renderSlopeTileGPU(
  paddedDem: Uint8ClampedArray,
  paddedSize: number,
  tileSize: number,
  z: number,
  y: number
): Uint8ClampedArray | null {
  if (!webglTileRenderer.available && !webglTileRenderer.init()) return null

  const lat = tileToLat(y + 0.5, z)
  const cellSize = metersPerPixel(lat, z)

  const prepareProgram = webglTileRenderer.compileProgram(FRAGMENT_PREPARE, PROGRAM_KEY_PREPARE)
  if (!prepareProgram) return null

  const demTexture = webglTileRenderer.uploadTexture(paddedDem, paddedSize, paddedSize, true)
  if (!demTexture) return null

  let slopeTexture: WebGLTexture | null = null
  try {
    slopeTexture = webglTileRenderer.renderToTexture(
      prepareProgram,
      {
        u_cellSize: cellSize,
        u_pixelSize: [1 / paddedSize, 1 / paddedSize],
        u_tileSize: tileSize,
        u_paddedSize: paddedSize,
      },
      {
        u_dem: { texture: demTexture, unit: 0 },
      },
      tileSize,
      tileSize
    )
  } finally {
    webglTileRenderer.deleteTexture(demTexture)
  }

  if (!slopeTexture) return null

  const displayProgram = webglTileRenderer.compileProgram(FRAGMENT_DISPLAY, PROGRAM_KEY_DISPLAY)
  if (!displayProgram) {
    webglTileRenderer.deleteTexture(slopeTexture)
    return null
  }

  try {
    return webglTileRenderer.renderToPixels(
      displayProgram,
      {},
      {
        u_slopeMap: { texture: slopeTexture, unit: 0 },
      },
      tileSize,
      tileSize
    )
  } finally {
    webglTileRenderer.deleteTexture(slopeTexture)
  }
}
